import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { X, Calendar, MapPin, Users } from 'lucide-react';
import { getApiUrl } from '@/lib/utils';

const API_URL = getApiUrl();

export function EditMatchModal({ match, onClose, onSaved }) {
  const [formData, setFormData] = useState({
    date: match?.date || '',
    venue: match?.venue || '',
    opponent: match?.opponent || '',
    status: match?.status || 'upcoming',
    max_players: match?.max_players || 24
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (match) {
      setFormData({
        date: match.date || '',
        venue: match.venue || '',
        opponent: match.opponent || '',
        status: match.status || 'upcoming',
        max_players: match.max_players || 24
      });
    }
  }, [match]);

  const handleSave = async () => {
    if (!formData.date) {
      toast.error('Date is required');
      return;
    }

    setLoading(true);
    try {
      const updatePayload = {
        date: formData.date,
        venue: formData.venue || null,
        status: formData.status
      };
      
      // Only include opponent for Saturday matches
      if (match.match_type === 'saturday') {
        updatePayload.opponent = formData.opponent || null;
      }
      
      // Include max_players for Sunday matches if changed
      if (match.match_type === 'sunday' && formData.max_players !== match.max_players) {
        updatePayload.max_players = parseInt(formData.max_players) || 24;
      }

      await axios.put(
        `${API_URL}/api/matches/${match.match_id}`,
        updatePayload,
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      
      toast.success('Match updated!');
      onSaved();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update match');
    } finally {
      setLoading(false);
    }
  };

  if (!match) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full overflow-hidden shadow-xl">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-[#0F172A] text-white">
          <h3 className="font-bold text-lg">
            Edit {match.match_type === 'saturday' ? 'Saturday' : 'Sunday'} Match
          </h3>
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/10"
            data-testid="close-edit-modal"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          {/* Date */}
          <div>
            <Label htmlFor="date" className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Date
            </Label>
            <Input
              id="date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="mt-1"
              data-testid="edit-date-input"
            />
          </div>

          {/* Venue */}
          <div>
            <Label htmlFor="venue" className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Venue
            </Label>
            <Input
              id="venue"
              type="text"
              placeholder="Enter venue"
              value={formData.venue}
              onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
              className="mt-1"
              data-testid="edit-venue-input"
            />
          </div>

          {/* Opponent (Saturday only) */}
          {match.match_type === 'saturday' && (
            <div>
              <Label htmlFor="opponent" className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Opponent
              </Label>
              <Input
                id="opponent"
                type="text"
                placeholder="Enter opponent team name"
                value={formData.opponent}
                onChange={(e) => setFormData({ ...formData, opponent: e.target.value })}
                className="mt-1"
                data-testid="edit-opponent-input"
              />
            </div>
          )}

          {/* Max Players (Sunday only) */}
          {match.match_type === 'sunday' && (
            <div>
              <Label htmlFor="max_players" className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Max Players
              </Label>
              <Input
                id="max_players"
                type="number"
                min="24"
                placeholder="24"
                value={formData.max_players}
                onChange={(e) => setFormData({ ...formData, max_players: e.target.value })}
                className="mt-1"
                data-testid="edit-max-players-input"
              />
              <p className="text-xs text-slate-500 mt-1">Default is 24. Set higher to allow more players.</p>
            </div>
          )}

          {/* Status */}
          <div>
            <Label className="text-sm font-semibold text-slate-700">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value })}
            >
              <SelectTrigger className="mt-1" data-testid="edit-status-select">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-slate-200 flex gap-3 justify-end bg-slate-50">
          <Button
            onClick={onClose}
            variant="outline"
            data-testid="cancel-edit-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-[#E63946] hover:bg-[#DC2626] text-white"
            data-testid="save-edit-btn"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
